import { Layout, Card, Grid, Spin, Button, Tooltip } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { Outlet } from "react-router-dom";
import Header from "../../components/Layout/Header";
import Sidebar from "../../components/Layout/Sidebar";
import PopupChatbox from "../../components/Chatbox/Chatbox";
import { useEffect, useState } from "react";
import TermsAndConditionsModal from "../../components/Modals/TermsAndConditionsModal";
import { useUser } from "../../context/UserContext";
import {
  acceptMyTermsAndConditions,
  getMyTermsAndConditionsAcceptance,
} from "../../utils/termsService";
import { Notify } from "../../utils/constants";

const { Header: AntHeader, Sider: AntSider, Content: AntContent } = Layout;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean>(false);
  const [isCheckingAcceptance, setIsCheckingAcceptance] =
    useState<boolean>(true);
  const {
    isAuth,

    clearAuth,
  } = useUser();
  const screens = Grid.useBreakpoint?.() || {};
  const isMobile = !screens.lg;
  const shouldShowTermsModal =
    isAuth && !isCheckingAcceptance && !hasAcceptedTerms;

  useEffect(() => {
    if (!isAuth) {
      setHasAcceptedTerms(false);
      setIsCheckingAcceptance(false);
      return;
    }

    const checkAcceptance = async () => {
      setIsCheckingAcceptance(true);
      try {
        const accepted = await getMyTermsAndConditionsAcceptance();
        setHasAcceptedTerms(accepted);
      } catch (error) {
        console.error("Failed to fetch terms acceptance:", error);
        setHasAcceptedTerms(false);
        Notify("error", "Could not verify Terms and Conditions acceptance.");
      } finally {
        setIsCheckingAcceptance(false);
      }
    };

    checkAcceptance();
  }, [isAuth]);

  const handleDeclineTerms = () => {
    clearAuth();
  };

  const handleAcceptTerms = async () => {
    await acceptMyTermsAndConditions();
    setHasAcceptedTerms(true);
  };

  if (isAuth && isCheckingAcceptance) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (isAuth && !hasAcceptedTerms) {
    return (
      <Layout className="h-screen overflow-hidden">
        <TermsAndConditionsModal
          open={shouldShowTermsModal}
          onAccept={handleAcceptTerms}
          onDecline={handleDeclineTerms}
        />
      </Layout>
    );
  }

  return (
    <Layout className="h-screen overflow-hidden">
      <AntHeader className="!h-[60px] bg-transparent" style={{ padding: 0 }}>
        <Header />
      </AntHeader>
      <Layout className="min-h-0">
        <AntSider
          className="app-sider"
          theme="light"
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          onBreakpoint={(broken) => setCollapsed(broken)}
          breakpoint="lg"
          collapsedWidth={isMobile ? 0 : 72}
          width={248}
          trigger={null}
        >
          <Tooltip
            title={collapsed ? "Expand navigation" : "Collapse navigation"}
            placement="right"
          >
            <Button
              className="app-sider__toggle"
              type="default"
              shape="circle"
              aria-label={
                collapsed ? "Expand navigation" : "Collapse navigation"
              }
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed((value) => !value)}
            />
          </Tooltip>
          <Sidebar
            collapsed={collapsed}
            onNavigate={isMobile ? () => setCollapsed(true) : undefined}
          />
        </AntSider>
        <AntContent className="min-h-0 overflow-y-auto p-4 my-2">
          <Card>
            <Outlet />
          </Card>
        </AntContent>

        <PopupChatbox />
      </Layout>

      <TermsAndConditionsModal
        open={shouldShowTermsModal}
        onAccept={handleAcceptTerms}
        onDecline={handleDeclineTerms}
      />
    </Layout>
  );
};

export default MainLayout;
